import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Chrome, Loader2, Phone, MapPin, Calendar, Apple, Trophy, Users, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';
import { toast } from '@/hooks/use-toast';
import { SkillLevel, SKILL_LEVEL_LABELS } from '@/components/SkillLevelBadge';
import { Sport, SPORT_LABELS } from '@/types';
import { z } from 'zod';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import pickleballIcon from '@/assets/pickleball-icon.png';
import logoImg from '@/assets/logo.png';
import { SITE_URL } from '@/lib/constants';
import { PhoneOTPDialog } from '@/components/auth/PhoneOTPDialog';

const AVAILABLE_SKILL_LEVELS: SkillLevel[] = ['beginner', 'amateur', 'semi_pro', 'professional'];
const AVAILABLE_SPORTS: Sport[] = ['football', 'volleyball', 'badminton', 'tabletennis', 'tennis', 'pickleball', 'basketball', 'swimming', 'golf', 'billiards', 'running', 'cycling'];

const sportEmojis: Record<Sport, string> = {
  football: '⚽',
  volleyball: '🏐',
  badminton: '🏸',
  tabletennis: '🏓',
  tennis: '🎾',
  pickleball: '',
  basketball: '🏀',
  swimming: '🏊',
  golf: '⛳',
  billiards: '🎱',
  running: '🏃',
  cycling: '🚴',
};

const VIETNAM_CITIES = [
  'Hà Nội', 'TP. Hồ Chí Minh', 'Đà Nẵng', 'Hải Phòng', 'Cần Thơ',
  'An Giang', 'Bà Rịa - Vũng Tàu', 'Bắc Giang', 'Bắc Kạn', 'Bạc Liêu',
  'Bắc Ninh', 'Bến Tre', 'Bình Định', 'Bình Dương', 'Bình Phước',
  'Bình Thuận', 'Cà Mau', 'Cao Bằng', 'Đắk Lắk', 'Đắk Nông',
  'Điện Biên', 'Đồng Nai', 'Đồng Tháp', 'Gia Lai', 'Hà Giang',
  'Hà Nam', 'Hà Tĩnh', 'Hải Dương', 'Hậu Giang', 'Hòa Bình',
  'Hưng Yên', 'Khánh Hòa', 'Kiên Giang', 'Kon Tum', 'Lai Châu',
  'Lâm Đồng', 'Lạng Sơn', 'Lào Cai', 'Long An', 'Nam Định',
  'Nghệ An', 'Ninh Bình', 'Ninh Thuận', 'Phú Thọ', 'Phú Yên',
  'Quảng Bình', 'Quảng Nam', 'Quảng Ngãi', 'Quảng Ninh', 'Quảng Trị',
  'Sóc Trăng', 'Sơn La', 'Tây Ninh', 'Thái Bình', 'Thái Nguyên',
  'Thanh Hóa', 'Thừa Thiên Huế', 'Tiền Giang', 'Trà Vinh', 'Tuyên Quang',
  'Vĩnh Long', 'Vĩnh Phúc', 'Yên Bái'
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Nam' },
  { value: 'female', label: 'Nữ' },
  { value: 'other', label: 'Khác' },
];

const currentYear = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: currentYear - 10 - 1940 + 1 }, (_, i) => currentYear - 10 - i);

const loginSchema = z.object({
  email: z.string().email('Email không hợp lệ').max(255, 'Email quá dài'),
  password: z.string().min(6, 'Mật khẩu phải ít nhất 6 ký tự'),
});

const signupSchema = z.object({
  email: z.string().email('Email không hợp lệ').max(255, 'Email quá dài'),
  password: z.string()
    .min(8, 'Mật khẩu phải ít nhất 8 ký tự')
    .max(72, 'Mật khẩu quá dài')
    .regex(/[A-Z]/, 'Mật khẩu phải có ít nhất 1 chữ hoa')
    .regex(/[0-9]/, 'Mật khẩu phải có ít nhất 1 chữ số'),
  name: z.string().min(2, 'Tên quá ngắn').max(100, 'Tên quá dài').trim(),
  phoneNumber: z.string().regex(/^(0[3|5|7|8|9])+([0-9]{8})$/, 'Số điện thoại không hợp lệ'),
  birthYear: z.number().min(1940, 'Năm sinh không hợp lệ').max(currentYear - 10, 'Bạn phải từ 10 tuổi trở lên'),
  gender: z.enum(['male', 'female', 'other'], { required_error: 'Vui lòng chọn giới tính' }),
  city: z.string().min(1, 'Vui lòng chọn nơi sống'),
});

// Floating sport icons for brand panel
const FLOATING_ICONS = ['⚽', '🏸', '🎾', '🏀', '🏐', '🏓', '⛳', '🏊', '🚴', '🏃'];

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [birthYear, setBirthYear] = useState<number | ''>('');
  const [gender, setGender] = useState<'male' | 'female' | 'other' | ''>('');
  const [city, setCity] = useState('');
  const [skillLevel, setSkillLevel] = useState<SkillLevel>('amateur');
  const [rankedSport, setRankedSport] = useState<Sport | ''>('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Where to send the user after successful auth — preserves deep link target.
  const fromState = (location.state as { from?: string } | null)?.from;
  const redirectTarget =
    fromState && fromState.startsWith('/') && !fromState.startsWith('/auth')
      ? fromState
      : '/';
  // Absolute URL used by OAuth redirect (must be a full URL on current origin so it
  // works on both preview domain and custom domain).
  const oauthRedirectUri =
    typeof window !== 'undefined'
      ? `${window.location.origin}${redirectTarget}`
      : `${SITE_URL}${redirectTarget}`;

  const validateForm = (): boolean => {
    setErrors({});
    try {
      if (isLogin) {
        loginSchema.parse({ email, password });
      } else {
        signupSchema.parse({ 
          email, password, name, phoneNumber,
          birthYear: birthYear || 0,
          gender: gender || undefined,
          city,
        });
      }
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as string;
          newErrors[field] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!isLogin && !agreedToTerms) {
      toast({
        title: "Lỗi",
        description: "Bạn cần đồng ý với Điều khoản dịch vụ và Chính sách bảo mật để tạo tài khoản.",
        variant: "destructive",
      });
      return;
    }

    if (isLogin) {
      setLoading(true);
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(), password,
        });
        if (error) throw error;
        toast({ title: "Đăng nhập thành công!" });
        navigate(redirectTarget, { replace: true });
      } catch (error: any) {
        toast({ title: "Lỗi", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Sign-up: require ranked sport, then open OTP dialog for phone verification
    if (!rankedSport) {
      setErrors({ rankedSport: 'Vui lòng chọn môn thể thao tính điểm' });
      return;
    }
    setOtpDialogOpen(true);
  };

  const completeSignup = async (verificationId: string) => {
    setOtpDialogOpen(false);
    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim(), password,
        options: {
          emailRedirectTo: `${SITE_URL}/`,
          data: {
            name: name.trim(), skill_level: skillLevel, ranked_sport: rankedSport,
            birth_year: birthYear || null, gender: gender || null,
            city: city || null, phone_number: phoneNumber || null,
            phone_verification_id: verificationId,
          },
        },
      });
      if (error) throw error;
      toast({
        title: "Đăng ký thành công!",
        description: "Vui lòng kiểm tra email để xác nhận tài khoản trước khi đăng nhập.",
      });
      setIsLogin(true);
      setPassword('');
    } catch (error: any) {
      toast({ title: "Lỗi", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: oauthRedirectUri });
    if (result?.error) {
      toast({ title: "Lỗi", description: result.error.message || "Đăng nhập Google thất bại", variant: "destructive" });
    }
  };

  const handleAppleLogin = async () => {
    const result = await lovable.auth.signInWithOAuth("apple", { redirect_uri: oauthRedirectUri });
    if (result?.error) {
      toast({ title: "Lỗi", description: result.error.message || "Đăng nhập Apple thất bại", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* ===== BRAND PANEL (Left / Top on mobile) ===== */}
      <div className="relative md:w-[45%] w-full md:min-h-screen overflow-hidden flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #001F2D 0%, #002847 40%, #0033A0 100%)' }}
      >
        {/* Animated floating sport icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {FLOATING_ICONS.map((icon, i) => (
            <span
              key={i}
              className="absolute text-2xl md:text-3xl opacity-[0.12] select-none"
              style={{
                left: `${10 + (i * 9) % 80}%`,
                top: `${5 + (i * 13) % 85}%`,
                animation: `auth-float ${6 + i * 0.7}s ease-in-out infinite alternate`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              {icon}
            </span>
          ))}
        </div>

        {/* Glow effects */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-[100px] opacity-30"
          style={{ background: 'radial-gradient(circle, #00B4FF 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full blur-[80px] opacity-20"
          style={{ background: 'radial-gradient(circle, #0033A0 0%, transparent 70%)' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-8 py-12 md:py-0 text-center">
          {/* Logo */}
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center mb-6"
            style={{ animation: 'auth-pulse 3s ease-in-out infinite' }}
          >
            <img src={logoImg} alt="SportRank" className="w-14 h-14 md:w-16 md:h-16 object-contain" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
            Sport<span style={{ color: '#00B4FF' }}>Rank</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg mb-8 max-w-xs">
            Kết nối đam mê thể thao
          </p>

          {/* Stats - hidden on mobile for compactness */}
          <div className="hidden md:flex gap-6 mt-4">
            {[
              { icon: Users, label: 'Người chơi', value: '10K+' },
              { icon: Trophy, label: 'Trận đấu', value: '50K+' },
              { icon: Star, label: 'Đánh giá', value: '4.8' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center gap-1.5">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-[#00B4FF]" />
                </div>
                <span className="text-white font-bold text-lg">{stat.value}</span>
                <span className="text-white/85 text-xs">{stat.label}</span>
              </div>
            ))}
          </div>

          {/* Neon accent line */}
          <div className="mt-8 w-24 h-0.5 rounded-full"
            style={{ background: 'linear-gradient(90deg, transparent, #00B4FF, transparent)' }} />
        </div>
      </div>

      {/* ===== FORM PANEL (Right / Bottom on mobile) ===== */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-8 md:px-12 md:py-6 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile-only small logo */}
          <div className="md:hidden text-center mb-4">
            <p className="text-sm text-muted-foreground">Chào mừng bạn đến với SportRank</p>
          </div>

          {/* Tabs */}
          <div className="flex mb-6 p-1 bg-muted rounded-xl">
            <button
              onClick={() => setIsLogin(true)}
              className={cn(
                'flex-1 py-3 text-sm font-semibold rounded-lg transition-all duration-300',
                isLogin 
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30' 
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Đăng nhập
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={cn(
                'flex-1 py-3 text-sm font-semibold rounded-lg transition-all duration-300',
                !isLogin 
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30' 
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Đăng ký
            </button>
          </div>

          {/* Social Login */}
          <div className="flex gap-3 mb-6">
            <Button variant="outline" className="flex-1 h-12 rounded-xl border-2 hover:border-primary/50 transition-colors" onClick={handleGoogleLogin}>
              <Chrome className="w-5 h-5 text-red-500" />
              <span className="ml-2 font-medium">Google</span>
            </Button>
            <Button variant="outline" className="flex-1 h-12 rounded-xl border-2 hover:border-primary/50 transition-colors" onClick={handleAppleLogin}>
              <Apple className="w-5 h-5" />
              <span className="ml-2 font-medium">Apple</span>
            </Button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">Hoặc</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[55vh] overflow-y-auto pr-1 auth-form-scroll">
            {!isLogin && (
              <>
                <div className="space-y-1 animate-fade-in" style={{ animationDelay: '0ms' }}>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-muted-foreground" />
                    <Input
                      type="text" placeholder="Họ và tên *" value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={cn("pl-11 h-12 rounded-xl", errors.name && "border-destructive")}
                      required={!isLogin}
                    />
                  </div>
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-1 animate-fade-in" style={{ animationDelay: '50ms' }}>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-muted-foreground" />
                    <Input
                      type="tel" placeholder="Số điện thoại * (VD: 0912345678)" value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className={cn("pl-11 h-12 rounded-xl", errors.phoneNumber && "border-destructive")}
                      required={!isLogin}
                    />
                  </div>
                  {errors.phoneNumber && <p className="text-xs text-destructive">{errors.phoneNumber}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3 animate-fade-in" style={{ animationDelay: '100ms' }}>
                  <div className="space-y-1">
                    <Select value={birthYear?.toString() || ''} onValueChange={(v) => setBirthYear(parseInt(v))}>
                      <SelectTrigger className={cn("h-12 rounded-xl", errors.birthYear && "border-destructive")}>
                        <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Năm sinh *" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {BIRTH_YEARS.map((year) => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.birthYear && <p className="text-xs text-destructive">{errors.birthYear}</p>}
                  </div>
                  <div className="space-y-1">
                    <Select value={gender} onValueChange={(v) => setGender(v as 'male' | 'female' | 'other')}>
                      <SelectTrigger className={cn("h-12 rounded-xl", errors.gender && "border-destructive")}>
                        <SelectValue placeholder="Giới tính *" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDER_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.gender && <p className="text-xs text-destructive">{errors.gender}</p>}
                  </div>
                </div>

                <div className="space-y-1 animate-fade-in" style={{ animationDelay: '150ms' }}>
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger className={cn("h-12 rounded-xl", errors.city && "border-destructive")}>
                      <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                      <SelectValue placeholder="Tỉnh/Thành phố *" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {VIETNAM_CITIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
                </div>

                <div className="space-y-2 animate-fade-in" style={{ animationDelay: '200ms' }}>
                  <Label className="text-sm text-muted-foreground">Môn thể thao tính điểm *</Label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {AVAILABLE_SPORTS.map((sport) => (
                      <label
                        key={sport}
                        className={cn(
                          "flex flex-col items-center justify-center p-2 rounded-lg border cursor-pointer transition-all text-sm gap-0.5",
                          rankedSport === sport 
                            ? "border-primary bg-primary/10 text-foreground shadow-sm shadow-primary/20" 
                            : "border-border hover:border-primary/50 text-muted-foreground"
                        )}
                      >
                        <input type="radio" name="ranked_sport" value={sport}
                          checked={rankedSport === sport} onChange={() => setRankedSport(sport)} className="sr-only" />
                        <span className="text-base">
                          {sport === 'pickleball' ? (
                            <img src={pickleballIcon} alt="Pickleball" className="w-4 h-4 inline-block" />
                          ) : sportEmojis[sport]}
                        </span>
                        <span className="text-[10px] leading-tight text-center">{SPORT_LABELS[sport]}</span>
                      </label>
                    ))}
                  </div>
                  {errors.rankedSport && <p className="text-xs text-destructive">{errors.rankedSport}</p>}
                  <p className="text-xs text-muted-foreground">Bạn chỉ có thể thi đấu rank với môn thể thao đã đăng ký</p>
                </div>

                <div className="space-y-2 animate-fade-in" style={{ animationDelay: '250ms' }}>
                  <Label className="text-sm text-muted-foreground">Trình độ chơi</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_SKILL_LEVELS.map((level) => (
                      <label
                        key={level}
                        className={cn(
                          "flex items-center justify-center p-2.5 rounded-lg border cursor-pointer transition-all text-sm",
                          skillLevel === level 
                            ? "border-primary bg-primary/10 text-foreground shadow-sm shadow-primary/20" 
                            : "border-border hover:border-primary/50 text-muted-foreground"
                        )}
                      >
                        <input type="radio" name="skill_level" value={level}
                          checked={skillLevel === level} onChange={() => setSkillLevel(level)} className="sr-only" />
                        <span>{SKILL_LEVEL_LABELS[level]}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Bạn chỉ có thể thi đấu rank với người chơi cùng trình độ</p>
                </div>
              </>
            )}

            <div className="space-y-1">
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-muted-foreground" />
                <Input
                  type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn("pl-11 h-12 rounded-xl", errors.email && "border-destructive")}
                  required
                />
              </div>
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-1">
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-muted-foreground" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isLogin ? "Mật khẩu" : "Mật khẩu (ít nhất 8 ký tự, 1 chữ hoa, 1 số)"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className={cn("pl-11 pr-11 h-12 rounded-xl", errors.password && "border-destructive")}
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
            </div>

            {isLogin && (
              <div className="text-right">
                <button type="button" className="text-sm text-primary hover:underline font-medium"
                  disabled={forgotPasswordLoading}
                  onClick={async () => {
                    if (!email.trim()) { setErrors({ email: 'Vui lòng nhập email trước' }); return; }
                    setForgotPasswordLoading(true);
                    try {
                      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                        redirectTo: `${SITE_URL}/reset-password`,
                      });
                      if (error) throw error;
                      toast({ title: 'Email đã được gửi', description: 'Vui lòng kiểm tra hộp thư để đặt lại mật khẩu.' });
                    } catch (err: any) {
                      toast({ title: 'Lỗi', description: err.message, variant: 'destructive' });
                    } finally { setForgotPasswordLoading(false); }
                  }}
                >
                  {forgotPasswordLoading ? 'Đang gửi...' : 'Quên mật khẩu?'}
                </button>
              </div>
            )}

            {!isLogin && (
              <div className="flex items-start space-x-3 py-2">
                <Checkbox id="terms" checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked === true)} className="mt-0.5" />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                  Tôi đã đọc và đồng ý với{' '}
                  <Link to="/terms" className="text-primary hover:underline font-medium">Điều khoản dịch vụ</Link>
                  {' '}và{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline font-medium">Chính sách bảo mật</Link>
                </label>
              </div>
            )}

            <Button type="submit" variant="gradient" className="w-full h-12 rounded-xl text-base font-semibold"
              size="lg" disabled={loading || (!isLogin && !agreedToTerms)}>
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Đăng nhập' : 'Tạo tài khoản'}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </form>

          {/* Footer terms */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            Bằng việc tiếp tục, bạn đồng ý với{' '}
            <Link to="/terms" className="text-primary hover:underline">Điều khoản dịch vụ</Link>
            {' '}và{' '}
            <Link to="/privacy-policy" className="text-primary hover:underline">Chính sách bảo mật</Link>
          </p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes auth-float {
          0% { transform: translateY(0) rotate(0deg); }
          100% { transform: translateY(-20px) rotate(8deg); }
        }
        @keyframes auth-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0,180,255,0.2); }
          50% { transform: scale(1.03); box-shadow: 0 0 30px 5px rgba(0,180,255,0.15); }
        }
        .auth-form-scroll::-webkit-scrollbar { width: 4px; }
        .auth-form-scroll::-webkit-scrollbar-track { background: transparent; }
        .auth-form-scroll::-webkit-scrollbar-thumb { background: hsl(var(--border)); border-radius: 4px; }
      `}</style>

      <PhoneOTPDialog
        open={otpDialogOpen}
        phone={phoneNumber}
        onClose={() => setOtpDialogOpen(false)}
        onVerified={completeSignup}
      />
    </div>
  );
}
